import { jsPDF } from 'jspdf';
import { autoTable } from 'jspdf-autotable';
import abroadifyLogo from '../assets/img/logo-large.png';

const getPageWidth = (doc) => doc.internal.pageSize.getWidth();
const getPageHeight = (doc) => doc.internal.pageSize.getHeight();

const loadImage = (src) =>
    new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = reject;
        img.src = src;
    });

const addPdfHeader = async (doc) => {
    try {
        const img = await loadImage(abroadifyLogo);
        const maxWidth = 58;
        const maxHeight = 20;
        const imgRatio = img.width / img.height;
        let renderWidth = maxWidth;
        let renderHeight = renderWidth / imgRatio;
        if (renderHeight > maxHeight) {
            renderHeight = maxHeight;
            renderWidth = renderHeight * imgRatio;
        }
        const x = (getPageWidth(doc) - renderWidth) / 2;
        doc.addImage(abroadifyLogo, 'PNG', x, 10, renderWidth, renderHeight);
    } catch {
        doc.setFontSize(18);
        doc.text('ABROADIFY', getPageWidth(doc) / 2, 20, { align: 'center' });
    }
    doc.setDrawColor(192, 217, 238);
    doc.line(14, 32, getPageWidth(doc) - 14, 32);
};

const addPdfFooter = (doc) => {
    const issuingDate = new Date().toLocaleDateString();
    doc.setDrawColor(192, 217, 238);
    const pageHeight = getPageHeight(doc);
    const footerY = pageHeight - 10;
    const lineY = pageHeight - 17;
    const rightX = getPageWidth(doc) - 14;
    doc.line(14, lineY, rightX, lineY);
    doc.setFontSize(9);
    doc.text(`Issuing Date: ${issuingDate}`, 14, footerY);
    doc.textWithLink('app.abroadify.me', rightX, footerY, {
        align: 'right',
        url: 'https://app.abroadify.me',
    });
    doc.setTextColor(0, 0, 0);
};

const addHeaderAndFooterToAllPages = async (doc) => {
    const totalPages = doc.getNumberOfPages();
    for (let page = 1; page <= totalPages; page += 1) {
        doc.setPage(page);
        await addPdfHeader(doc);
        addPdfFooter(doc);
    }
};

const formatCount = (value) => (typeof value === 'number' ? value.toLocaleString() : '—');

const addSectionTable = (doc, title, rows, startY) => {
    if (!rows.length) return startY;

    if (startY > 240) {
        doc.addPage();
        startY = 42;
    }

    doc.setFontSize(13);
    doc.setTextColor(20, 20, 20);
    doc.text(title, 14, startY);
    doc.setTextColor(0, 0, 0);

    autoTable(doc, {
        startY: startY + 4,
        head: [['Field', 'Value']],
        body: rows.map(([label, value]) => [label, formatCount(value)]),
        theme: 'grid',
        styles: {
            fontSize: 9,
            cellPadding: 2.5,
            overflow: 'linebreak',
            valign: 'top',
            lineColor: [192, 217, 238],
            lineWidth: 0.3,
            textColor: [45, 45, 45],
        },
        headStyles: {
            fontSize: 10,
            fillColor: [240, 248, 255],
            textColor: [26, 26, 26],
            lineColor: [192, 217, 238],
            lineWidth: 0.3,
            fontStyle: 'bold',
        },
        bodyStyles: {
            lineColor: [192, 217, 238],
            lineWidth: 0.3,
        },
        alternateRowStyles: {
            fillColor: [255, 255, 255],
        },
        columnStyles: {
            0: { cellWidth: 55 },
            1: { cellWidth: 127 },
        },
        margin: { top: 42, left: 14, right: 14, bottom: 20 },
    });

    return doc.lastAutoTable.finalY + 10;
};

const formatDisplayDate = (isoDate) => {
    if (!isoDate) return '';
    const date = new Date(isoDate);
    if (Number.isNaN(date.getTime())) return '';
    return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
};

export const exportReportingGeneralPdf = async ({
    generalStats,
    activityStats,
    companyStats,
    fromDate,
    toDate,
}) => {
    const doc = new jsPDF();
    let y = 42;

    doc.setFontSize(18);
    doc.text('General Reporting', 14, y);
    y += 8;

    doc.setFontSize(10);
    const period = fromDate || toDate
        ? `Period: ${fromDate ? formatDisplayDate(fromDate) : '—'} to ${toDate ? formatDisplayDate(toDate) : '—'}`
        : 'Period: All time';
    doc.text(period, 14, y);
    y += 6;
    doc.text(`Exported: ${new Date().toLocaleString()}`, 14, y);
    y += 10;

    y = addSectionTable(doc, 'Users', [
        ['Total Users', generalStats?.totalUsers],
        ['Talent', generalStats?.userClass?.talent],
        ['Company', generalStats?.userClass?.company],
        ['Partner', generalStats?.userClass?.partner],
        ['Abroadify', generalStats?.userClass?.abroadify],
        ['User', generalStats?.userTypes?.user],
        ['Admin', generalStats?.userTypes?.admin],
        ['Super Admin', generalStats?.userTypes?.superAdmin],
    ], y);

    y = addSectionTable(doc, 'Activities', [
        ['Total Activities', activityStats?.totalActivities],
        ['Company profile views (non-admins)', activityStats?.views?.companyProfileViews],
        ['Open job views', activityStats?.views?.openJobViews],
        ['City insights views', activityStats?.views?.cityInsightsViews],
        ['Salary insights views', activityStats?.views?.salaryInsightsViews],
        ['Comments posted', activityStats?.actions?.commentsPosted],
        ['Invited users', activityStats?.actions?.invitedUsers],
        ['Chats initiated / Messages exchanged', activityStats?.actions?.chatsMessages],
    ], y);

    y = addSectionTable(doc, 'Company Data', [
        ['Total Companies', companyStats?.totalCompanies],
        ['Continents', companyStats?.geo?.continents],
        ['Countries', companyStats?.geo?.countries],
        ['Cities', companyStats?.geo?.cities],
        ['Jobs posted', companyStats?.actions?.jobsPosted],
        ['Job applications sent', companyStats?.actions?.jobApplicationsSent],
    ], y);

    await addHeaderAndFooterToAllPages(doc);

    doc.save('general-reporting-export.pdf');
};
